import React, { useEffect, useRef, useState } from "react";
import { FileVisibility } from "../services/filesService";
import { shareUrl } from "../data/shareRouting";
import { copyTextToClipboard } from "../data/clipboard";
import {
  LockedIcon,
  UnlockedIcon,
  usersIcon,
} from "../../packages/excalidraw/components/icons";
import { glyph } from "./shareIcons";
import { ManualCopyField } from "./ManualCopyField";

/**
 * Share popover (v1) for a saved cloud file. Two controls:
 *
 *  - Visibility toggle — Private 🔒 / Public 🌐. Optimistic: the label flips
 *    immediately, and reverts if `onSetVisibility` (which persists via
 *    filesService) rejects.
 *  - Copy link — always copies the canonical `/share/<id>` URL (never the
 *    editor `/f/<id>` URL) and shows a transient "Link copied" confirmation.
 *    The Clipboard API isn't available everywhere (non-secure origins, older
 *    Safari), so "Link copied" is only ever shown when the copy actually
 *    succeeded — otherwise the URL is shown selectable so the user can copy
 *    it by hand.
 *
 * `embedSlot` is the extension point the next slice fills with the
 * "Embed in Notion" guided action.
 *
 * `onCollabDialogOpen`, when provided, renders a fourth action — "Live
 * collaborate" — that opens the existing encrypted collab-room flow
 * (`CollabDialog`, unchanged). This is the same handler the top-right
 * live-collaboration trigger and the main menu use; the popover is just
 * another entry point into it (issue #37: the popover is now the one share
 * surface for a cloud file, folding collab in rather than duplicating it).
 *
 * `fileId` is optional: when the canvas hasn't been saved to My Files yet,
 * `fileId` is omitted and the popover renders every share action in a
 * disabled, prompting state ("Save this mindmap to share it") instead of
 * hiding itself — the share feature should be discoverable even before the
 * first save. `onSaveRequest`, when provided, wires the prompt's button to
 * the same "Save to My Files" flow used elsewhere.
 */
export const SharePopover: React.FC<{
  fileId?: string;
  visibility: FileVisibility;
  /** Persists the new visibility (owner-only via RLS). Rejects → UI reverts. */
  onSetVisibility: (next: FileVisibility) => Promise<unknown>;
  /** Origin override for tests; defaults to the current window origin. */
  origin?: string;
  embedSlot?: React.ReactNode;
  /** Opens the existing collab-room dialog. Omitted → no action rendered. */
  onCollabDialogOpen?: () => void;
  /**
   * A room is live right now: the Live-collaborate row switches to the green
   * "active" treatment. It still opens the same dialog — that dialog already
   * offers "Stop session" when a room is active, so there's no second stop
   * control here.
   */
  isCollaborating?: boolean;
  /** Runs the "Save to My Files" flow. Omitted → no save prompt rendered. */
  onSaveRequest?: () => void;
}> = ({
  fileId,
  visibility,
  onSetVisibility,
  origin,
  embedSlot,
  onCollabDialogOpen,
  isCollaborating = false,
  onSaveRequest,
}) => {
  // Optimistic override of the prop; null = "show the prop value".
  const [optimistic, setOptimistic] = useState<FileVisibility | null>(null);
  const [error, setError] = useState<string | null>(null);
  // "idle" → default "Copy link" button; "copied" → transient success
  // confirmation; "failed" → the automatic copy didn't work (Clipboard API
  // missing/rejected) so the URL is shown selectable for a manual copy.
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the pending "Copied" reset on unmount — otherwise a popover closed
  // within the 2s window fires setCopied on an unmounted component.
  useEffect(
    () => () => {
      if (copyTimer.current) {
        clearTimeout(copyTimer.current);
      }
    },
    [],
  );

  const unsaved = !fileId;
  const shown = optimistic ?? visibility;
  const isPublic = shown === "public";

  const handleToggle = () => {
    if (unsaved) {
      return;
    }
    const next: FileVisibility = isPublic ? "private" : "public";
    setError(null);
    setOptimistic(next);
    Promise.resolve(onSetVisibility(next))
      .then(() => {
        // Success: prop will catch up to `next`; drop the local override.
        setOptimistic(null);
      })
      .catch((err) => {
        // Revert the optimistic flip.
        setOptimistic(null);
        setError("Couldn't update sharing. Try again.");
        // eslint-disable-next-line no-console
        console.error(err);
      });
  };

  const handleCopy = () => {
    if (unsaved || !fileId) {
      return;
    }
    const url = shareUrl(fileId, origin);
    void copyTextToClipboard(url).then((ok) => {
      if (copyTimer.current) {
        clearTimeout(copyTimer.current);
        copyTimer.current = null;
      }
      if (ok) {
        setCopyState("copied");
        copyTimer.current = setTimeout(() => setCopyState("idle"), 2000);
      } else {
        // Never claim success: show the URL selectable so the user can
        // copy it manually instead.
        setCopyState("failed");
      }
    });
  };

  return (
    <div
      className="share-popover"
      data-testid="share-popover"
      role="dialog"
      aria-label="Share this mindmap"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        minWidth: "18rem",
        padding: "1rem",
        borderRadius: "0.75rem",
        background: "var(--island-bg-color)",
        boxShadow: "var(--shadow-island, 0 2px 12px rgba(0,0,0,0.18))",
        color: "var(--text-primary-color)",
      }}
    >
      {unsaved && (
        <div
          data-testid="share-unsaved-prompt"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            padding: "0.6rem",
            borderRadius: "0.5rem",
            // `--background-color-hover` is not a token this app defines, so
            // the old value always fell back to a near-white #f3f3fb — a white
            // panel inside a dark popover, with theme-coloured (light) text on
            // top of it.
            background: "var(--color-surface-high)",
          }}
        >
          <span style={{ fontSize: "0.85rem" }}>
            Save this mindmap to share it
          </span>
          {onSaveRequest && (
            <button
              type="button"
              data-testid="share-save-now"
              onClick={onSaveRequest}
              style={{
                padding: "0.5rem 0.75rem",
                borderRadius: "0.5rem",
                border: "none",
                background: "var(--color-primary)",
                color: "var(--color-icon-white)",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              Save to My Files
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        data-testid="share-visibility-toggle"
        onClick={handleToggle}
        aria-pressed={isPublic}
        disabled={unsaved}
        title={unsaved ? "Save this mindmap to change sharing" : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          padding: "0.6rem",
          borderRadius: "0.5rem",
          border: "1px solid var(--default-border-color)",
          background: "transparent",
          color: "var(--text-primary-color)",
          cursor: unsaved ? "not-allowed" : "pointer",
          textAlign: "left",
          opacity: unsaved ? 0.6 : 1,
        }}
      >
        <span aria-hidden style={{ fontSize: "1.1rem", lineHeight: 0 }}>
          {isPublic
            ? glyph(UnlockedIcon, "1.1rem")
            : glyph(LockedIcon, "1.1rem")}
        </span>
        <span style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>
            {isPublic ? "Public" : "Private"}
          </span>
          <span
            style={{ fontSize: "0.8rem", color: "var(--share-text-muted)" }}
          >
            {isPublic
              ? "Anyone with the link can view"
              : "Only you can open this"}
          </span>
        </span>
      </button>

      {error && (
        <span
          role="alert"
          style={{ fontSize: "0.8rem", color: "var(--share-text-danger)" }}
        >
          {error}
        </span>
      )}

      <button
        type="button"
        data-testid="share-copy-link"
        onClick={handleCopy}
        disabled={unsaved}
        title={unsaved ? "Save this mindmap to copy a share link" : undefined}
        style={{
          padding: "0.55rem 0.75rem",
          borderRadius: "0.5rem",
          border: "none",
          // Disabled needs its own surface, not a faded accent: a 50%-opacity
          // primary fill is lavender-on-lavender in dark mode and unreadable.
          background: unsaved
            ? "var(--color-surface-high)"
            : "var(--color-primary)",
          color: unsaved
            ? "var(--text-primary-color)"
            : "var(--color-icon-white)",
          fontWeight: 600,
          fontSize: "0.9rem",
          cursor: unsaved ? "not-allowed" : "pointer",
          opacity: unsaved ? 0.6 : 1,
        }}
      >
        {copyState === "copied" ? "Link copied" : "Copy link"}
      </button>

      {copyState === "failed" && fileId && (
        <div
          data-testid="share-copy-manual"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.35rem",
          }}
        >
          <span
            style={{ fontSize: "0.8rem", color: "var(--share-text-danger)" }}
          >
            Couldn't copy automatically — copy the link below instead.
          </span>
          <ManualCopyField
            data-testid="share-copy-manual-input"
            value={shareUrl(fileId, origin)}
          />
        </div>
      )}

      {embedSlot ? <div data-testid="share-embed-slot">{embedSlot}</div> : null}

      {onCollabDialogOpen && (
        <button
          type="button"
          data-testid="share-live-collaborate"
          data-active={isCollaborating ? "true" : "false"}
          aria-pressed={isCollaborating}
          onClick={onCollabDialogOpen}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
            padding: "0.6rem",
            borderRadius: "0.5rem",
            // Same active pair as the ☰ menu's `.active-collab` row.
            border: `1px solid ${
              isCollaborating
                ? "var(--collab-active-bg)"
                : "var(--default-border-color)"
            }`,
            background: isCollaborating
              ? "var(--collab-active-bg)"
              : "transparent",
            color: isCollaborating
              ? "var(--collab-active-color)"
              : "var(--text-primary-color)",
            cursor: "pointer",
            textAlign: "left",
            fontWeight: 600,
            fontSize: "0.9rem",
          }}
        >
          <span aria-hidden style={{ fontSize: "1.1rem", lineHeight: 0 }}>
            {glyph(usersIcon, "1.1rem")}
          </span>
          {isCollaborating ? "Live collaboration · active" : "Live collaborate"}
        </button>
      )}
    </div>
  );
};

export default SharePopover;
